import ical from "ical.js";
import axios from "axios";
import {
  Features,
  getApiRoute,
  hasAccess,
  shouldUseSubscriptionExperience,
} from "src/services";
import { UAParser } from "ua-parser-js";
import { getPageRoute } from "src/services";
import abTestingService from "src/services/ABTestingService.js";
import { useRef, useCallback, useEffect, useState, useMemo } from "react";
import Vibrant from "node-vibrant";
import temporaryEmailsSet from "./temporaryEmails.js";
import * as ICAL from "ical.js";
import dayjs from "dayjs";
import * as imageCacheStorage from "src/utils/imageCacheStorage.js";
import { imagePalettes } from "src/utils/imageDecisionStore.js";
import { inkProfileFromPixels } from "@stanza/shared/color-contrast";
import { zonedWallClock } from "@stanza/shared/zoned-time";
import { canonicalImageKey, withParam } from "src/utils/imageUrl.js";
import {
  GOOGLE_CONNECT_STATES,
  googleConnectState,
} from "src/utils/googleConnect.js";

/**
 * Color quantization is statistical, so a small thumbnail yields the same
 * palette as the full image while keeping the canvas allocation tiny.
 */
const COLOR_EXTRACTION_MAX_DIMENSION = 128;

/** In-memory palette memo (LRU) covering derived blob/data sources the
 *  persisted store cannot key. */
const MAX_COLOR_EXTRACTIONS = 100;
const colorExtractionCache = new Map();

/** Keys longer than this are data URLs — never pinned as Map keys. */
const MAX_COLOR_CACHE_KEY_LENGTH = 2048;

/** In-flight work per key, so concurrent callers share one quantization. */
const colorExtractionRequests = new Map();

/** Neutral gray when node-vibrant cannot quantize a swatch (flat/monochrome sources). */
const FALLBACK_SWATCH_HEX = "#4a4a4a";

export const isEmptyCal = (events) =>
  events?.length === 1 &&
  events[0]?.jCal?.[1]
    ?.find((prop) => prop[0] === "description")?.[3]
    .includes(
      `has no upcoming events. Don't worry, as soon as new events are added - your calendar will be automatically updated.`
    );

// Helper function for getUpcomingEvents and getPastEvents
const getEventDates = (event, typeHomepage) => {
  const prop = typeHomepage ? event.event.jCal[1] : event.jCal[1];

  // Reads a property the way event cards display it; null when missing or unparseable.
  const getDtValue = (propName) => {
    const dateProp = prop.find((p) => p[0] === propName);
    if (typeof dateProp?.[3] !== "string" || !dateProp[3]) return null;
    const parsed = parseJcalDate(dateProp);
    return Number.isNaN(parsed.eventParsed.getTime()) ? null : parsed;
  };

  const start = getDtValue("dtstart");
  if (!start) {
    return { dtstart: null, dtend: null };
  }
  const { eventParsed: dtstart, isAllDayEvent: allDay } = start;

  // A DATE event without DTEND lasts one civil day. Timed events keep their
  // existing zero-duration fallback when an end is absent.
  const dtend =
    getDtValue("dtend")?.eventParsed ??
    (allDay
      ? new Date(dtstart.getFullYear(), dtstart.getMonth(), dtstart.getDate() + 1)
      : dtstart);

  return { dtstart, dtend, allDay };
};

/**
 * Every still-upcoming event, soonest first and uncapped, so a caller can slice
 * a list and quote a total without walking the feed twice.
 *
 * @param {Array} events - Parsed vevents.
 * @param {boolean} [typeHomepage] - Whether events carry the homepage wrapper shape.
 * @returns {Array} The matching vevents, sorted by start.
 */
export const selectUpcomingEvents = (events, typeHomepage) => {
  if (isEmptyCal(events)) {
    return [];
  }

  const now = new Date();
  const upcomingEvents = new Map();

  for (const event of events) {
    const { dtstart, dtend, allDay } = getEventDates(event, typeHomepage);
    if (!dtstart || !dtend) {
      continue;
    }
    if (allDay ? dtend > now : dtend >= now) {
      upcomingEvents.set(event, { dtstart, dtend });
    }
  }

  return Array.from(upcomingEvents.keys()).sort((event1, event2) => {
    if (
      upcomingEvents.get(event2).dtstart < upcomingEvents.get(event1).dtstart
    ) {
      return 1;
    } else if (
      upcomingEvents.get(event2).dtstart > upcomingEvents.get(event1).dtstart
    ) {
      return -1;
    }
    return 0;
  });
};

/**
 * The soonest `count` upcoming events.
 *
 * @param {Array} events - Parsed vevents.
 * @param {number} count - How many to keep.
 * @param {boolean} [typeHomepage] - Whether events carry the homepage wrapper shape.
 * @returns {Array}
 */
export const getUpcomingEvents = (events, count, typeHomepage) =>
  selectUpcomingEvents(events, typeHomepage).slice(0, count);

/**
 * Maps a jCal vevent to the shared SEO event shape, reusing the timeline's
 * date parsing so the crawlable copy matches the event list.
 *
 * @param {{ jCal?: Array }} event
 * @returns {{ name?: string, location?: string, allDay: boolean, allDayDate?: string, startDate?: Date, endDate?: Date }}
 */
export const toSeoEvent = (event) => {
  const props = event?.jCal?.[1] ?? [];
  const prop = (name) => props.find((p) => p?.[0] === name);
  const { dtstart, dtend, allDay = false } = props.length
    ? getEventDates(event)
    : { dtstart: null, dtend: null };
  return {
    name: prop("summary")?.[3],
    location: prop("location")?.[3],
    allDay,
    ...(allDay ? { allDayDate: prop("dtstart")?.[3] } : {}),
    startDate: dtstart ?? undefined,
    endDate: dtend ?? undefined,
  };
};

export const getPastEvents = (events, count) => {
  if (isEmptyCal(events)) {
    return [];
  }

  const now = new Date();
  const pastEvents = new Map();

  for (const event of events) {
    const { dtstart, dtend, allDay } = getEventDates(event);
    if (!dtstart || !dtend) {
      continue;
    }
    if (allDay ? dtend <= now : dtend < now) {
      pastEvents.set(event, { dtstart, dtend });
    }
  }

  const sortedEvents = Array.from(pastEvents.keys()).sort((event1, event2) => {
    if (pastEvents.get(event2).dtstart > pastEvents.get(event1).dtstart) {
      return 1;
    } else if (
      pastEvents.get(event2).dtstart < pastEvents.get(event1).dtstart
    ) {
      return -1;
    }
    return 0;
  });

  return sortedEvents.slice(0, count); // Only include the first {count} past events
};

// Helper function to combine different webcals into one
export const fetchCalendarData = async (
  calendars,
  setEvents,
  setIsLoading = () => { }
) => {
  setIsLoading(true);
  try {
    const responses = await Promise.allSettled(
      calendars.map(async (calendar) => {
        const response = await axios.get(
          getApiRoute("calendar", "GET_CALENDAR_EVENTS", {
            link: encodeURIComponent(calendar.stanzaLink),
          })
        );
        return { response, calendar: calendar, calendarId: calendar._id };
      })
    );

    // filter out errors from public webcals that are not available
    const fulfilledResponses = responses.filter(
      (response) => response.status === "fulfilled"
    );
    const events = fulfilledResponses.flatMap((response) => {
      try {
        const jcalData = ical.parse(response.value.response.data);
        const comp = new ical.Component(jcalData);
        const subcomponents = comp.getAllSubcomponents("vevent");
        return subcomponents.map((subcomponent) => ({
          event: subcomponent,
          calendar: response.value.calendar,
          calendarId: response.value.calendarId,
        }));
      } catch (error) {
        console.error(error);
        return [];
      }
    });

    setEvents(events);
    setIsLoading(false);
  } catch (error) {
    console.error(error);
    setEvents([]);
    setIsLoading(false);
  }
};

const getEventFromMostRecent = (mostRecentEvent) => {
  try {
    if (!mostRecentEvent) {
      return null;
    }

    const jcalData = ical.parse(
      `BEGIN:VCALENDAR\n${mostRecentEvent}\nEND:VCALENDAR`
    );
    const comp = new ical.Component(jcalData);
    const eventComponent = comp.getFirstSubcomponent("vevent");

    return eventComponent ? { event: eventComponent } : null;
  } catch (error) {
    console.error("Error parsing mostRecentEvent:", error);
    return null;
  }
};

const getEventMockFromCalendar = (calendar) => ({
  event: {
    jCal: [
      "vevent",
      [
        ["dtstart", {}, "date-time", "2025-02-24T00:30:00Z"],
        ["dtend", {}, "date-time", "2025-02-24T02:30:00Z"],
        ["uid", {}, "text", ""],
        ["summary", {}, "text", ""],
        [
          "description",
          {},
          "text",
          calendar.description !== "no description" && calendar.description
            ? calendar.description.slice(0, 70) + "..."
            : "",
        ],
        ["location", {}, "text", ""],
        ["status", {}, "text", "CONFIRMED"],
        ["dtstamp", {}, "date-time", "2025-02-22T03:17:13Z"],
        ["created", {}, "date-time", "2025-02-22T03:17:13Z"],
        ["last-modified", {}, "date-time", "2025-02-22T03:17:13Z"],
        ["transp", {}, "text", "OPAQUE"],
      ],
    ],
  },
  calendar: { ...calendar },
  calendarId: calendar._id,
});

export const getEventsFromRecentEvents = async (calendars, setEvents) => {
  let events = [];
  for (const calendar of calendars) {
    const needMock = calendar.mostRecentEvent === null;
    try {
      if (!needMock && typeof calendar.mostRecentEvent !== "string") {
        console.error(`Invalid mostRecentEvent for calendar ${calendar.name}`);
        continue;
      }

      const parsedEvent = getEventFromMostRecent(calendar.mostRecentEvent);
      events.push(
        parsedEvent
          ? {
            ...parsedEvent,
            calendar: { ...calendar },
            calendarId: calendar._id,
          }
          : getEventMockFromCalendar(calendar)
      );
    } catch (error) {
      console.error(`Error processing calendar ${calendar.name}:`, error);
    }
  }
  setEvents(events);
};

// Convert the calendars icsFiles to iCal objects and get the events to merge them into one array
export const getEventsFromIcsFiles = async (calendars, setEvents) => {
  let events = [];
  for (const calendar of calendars) {
    try {
      if (!calendar.icsFile || typeof calendar.icsFile !== "string") {
        console.error(`Invalid icsFile for calendar ${calendar.name}`);
        continue;
      }

      const jcalData = ical.parse(calendar.icsFile);
      const comp = new ical.Component(jcalData);
      const subcomponents = comp.getAllSubcomponents("vevent");

      if (!isEmptyCal(subcomponents)) {
        events = [
          ...events,
          ...subcomponents.map((subcomponent) => ({
            event: subcomponent,
            calendar: { ...calendar },
            calendarId: calendar._id,
          })),
        ];
      }
    } catch (error) {
      console.error(`Error processing calendar ${calendar.name}:`, error);
    }
  }
  setEvents(events);
};

export const handleSignInDialog = (
  user,
  setOpenSignInDialog,
  setSearchParams,
  extraSearchParams = {}
) => {
  if (!user) {
    // open the sign-in dialog
    const newSearchParams = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(extraSearchParams)) {
      newSearchParams.set(key, value);
    }
    window.history.replaceState({}, "", `?${newSearchParams.toString()}`);
    setOpenSignInDialog(true);
    return true;
  }
  return false;
};

// Helper function to redirect a signed out user to the Sign In page
export const redirectSignedOutUser = (
  user,
  navigate,
  extraSearchParams = {}
) => {
  const newSearchParams = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(extraSearchParams)) {
    newSearchParams.set(key, value);
  }
  if (!user) {
    navigate(
      getPageRoute(
        "auth",
        "SIGNIN",
        {},
        {
          forward: window.location.pathname + "?" + newSearchParams.toString(),
        }
      )
    );
    return true; // indicate that a redirected happened
  }
  return false; // indicate that no redirect happened
};

// Helper function to route users through the managed subscription page.
// That page is responsible for creating the Stripe customer portal session.
export const handleCustomerPortal = () => {
  window.location.href = getPageRoute("upgrade", "MANAGE_SUBSCRIPTION");
};

// Check if the calendar link is valid
export const validateCalendar = async (link) => {
  try {
    const response = await axios.get(
      getApiRoute("calendar", "GET_CALENDAR_EVENTS", {
        link: encodeURIComponent(link),
      }),
      { withCredentials: true }
    );
    if (response.status === 200) {
      console.log("It's a valid calendar link");
      return response;
    }
  } catch (error) {
    //return the error message
    return { error: "Webcal link is not valid or is not a public calendar" };
  }
};

// check if user has subscribed to the calendar
export const useCheckSubscriptionStatus = ({
  user = {},
  calendar = {},
} = {}) => {
  const [subscribed, setSubscribed] = useState(false);
  const {
    createdGroups = [],
    addedGroups = [],
    addedCalendars = [],
  } = user || {};
  const calendarId = calendar?._id ?? calendar?.id;

  // Memoize the Set to avoid recalculating on each render
  const createdGroupIds = useMemo(
    () => new Set(createdGroups.flatMap((group) => group.calendars)),
    [createdGroups]
  );

  // Determine subscription status
  const inGroup = createdGroupIds.has(calendarId);
  const addedGroup = addedGroups.includes(calendarId);
  const alreadyAddedCalendar = addedCalendars.includes(calendarId);

  useEffect(() => {
    setSubscribed(inGroup || addedGroup || alreadyAddedCalendar);
  }, [inGroup, addedGroup, alreadyAddedCalendar]);

  return {
    inGroup,
    addedGroup,
    alreadyAddedCalendar,
    subscribed,
    isSubscribed: () => inGroup || addedGroup || alreadyAddedCalendar,
    setSubscribed,
  };
};

/**
 * Subscriber count above this reads as real popularity — below it, tiles and
 * heroes prefer the next event or a status pill over the raw number.
 */
export const SUBSCRIBER_COUNT_THRESHOLD = 100;

// Helper function to format a number of subscribers. Enhanced with configurable thresholds and alternative text for low counts
export const formatSubscribers = (subscribers, options = {}) => {
  const {
    includeSubscriberText = true,
    showAlternativeText = false,
    createdAt = null,
    stableKey = null,
  } = options;

  const growingThreshold = SUBSCRIBER_COUNT_THRESHOLD;
  const newCalendarDays = 90;
  const isNewCalendar =
    createdAt &&
    new Date() - new Date(createdAt) < newCalendarDays * 24 * 60 * 60 * 1000;

  // Handle subscriber counts over growingTreshold
  if (subscribers > growingThreshold) {
    if (subscribers >= 1000) {
      const roundedSubscribers = (subscribers / 1000).toFixed(1);
      const formattedSubscribers = roundedSubscribers.endsWith(".0")
        ? Math.round(roundedSubscribers)
        : roundedSubscribers;
      return includeSubscriberText
        ? `${formattedSubscribers}K Subscribers`
        : `${formattedSubscribers}K`;
    } else {
      return includeSubscriberText
        ? `${subscribers} Subscribers`
        : `${subscribers}`;
    }
  } else {
    if (subscribers === 0) {
      return "";
    } else if (!showAlternativeText && subscribers === 1) {
      return includeSubscriberText ? `1 Subscriber` : 1;
    } else if (!showAlternativeText && subscribers > 1) {
      return includeSubscriberText
        ? `${subscribers} Subscribers`
        : `${subscribers}`;
    } else {
      if (isNewCalendar) {
        return "New on Stanza";
      } else {
        // Status message for non-new calendars: deterministic per `stableKey`
        // (e.g. the calendar handle) so re-renders don't reroll it; random
        // when no key is given.
        const statusMessages = ["On the Rise", "Building Buzz", "Fan Favorite"];
        const index = stableKey
          ? [...String(stableKey)].reduce((hash, char) => hash + char.charCodeAt(0), 0) %
            statusMessages.length
          : Math.floor(Math.random() * statusMessages.length);
        return statusMessages[index];
      }
    }
  }
};

// Helper function to sort an array of objects alphabetically
export const sortAlphabetically = (arr, targetKey = null) => {
  return arr.sort((a, b) => {
    if (targetKey) {
      return a[targetKey].localeCompare(b[targetKey]);
    }
    return a.localeCompare(b);
  });
};

export function useIsMounted() {
  const mountedRef = useRef(false);
  const isMounted = useCallback(() => mountedRef.current, []);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return isMounted;
}

// Helper function to turn https:// link to webcal:// link
export function getWebcalFormat(url) {
  if (!url) return null;
  return `webcal://${url.replace(/^https?:\/\//, "")}`;
}

/**
 * Generates a unique event number based on the last digit of the ID and position.
 * @param {string} id - The event ID.
 * @param {number} position - The position in the timeline.
 * @returns {string} The unique event number.
 */
export const getEventUniqueNumber = (id, position) => {
  const lastDigit = id
    ?.split("")
    .reverse()
    .find((char) => !isNaN(char));
  return lastDigit ? lastDigit + position : position.toString();
};

const loadImageForColorExtraction = async (imageUrl) => {
  const attempt = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = () =>
        reject(new Error("Image failed to load for color extraction."));
      image.src = url;
    });

  try {
    return await attempt(imageUrl);
  } catch (error) {
    // Hosts that only send CORS headers when asked (e.g. TMDB) may be
    // browser-cached without them by the plain <img>; a distinct query key
    // bypasses that cache entry so the anonymous request reaches the network.
    if (!imageUrl.startsWith("http")) throw error;
    return attempt(withParam(imageUrl, "palette", "1"));
  }
};

/**
 * Creates a bounded image source before node-vibrant loads it. node-vibrant's
 * maxDimension option is applied after its browser adapter creates a canvas,
 * so passing the original URL can still allocate a full-resolution canvas.
 *
 * @returns {Promise<{dataUrl: string, pixels: Uint8ClampedArray}>} The
 *   bounded source plus its RGBA bytes, which the ink profile reduces.
 */
const resizeImageForColorExtraction = async (imageUrl) => {
  const image = await loadImageForColorExtraction(imageUrl);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const scale = Math.min(
    1,
    COLOR_EXTRACTION_MAX_DIMENSION / Math.max(sourceWidth, sourceHeight)
  );
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Unable to create an image-processing canvas.");
  }

  canvas.width = width;
  canvas.height = height;
  context.drawImage(image, 0, 0, width, height);

  // Both reads throw on a tainted canvas, and the raw source is not a fallback:
  // node-vibrant would decode it at full resolution, which is what the bounded
  // canvas exists to avoid. Let it reject so the caller drops the palette.
  return {
    dataUrl: canvas.toDataURL(),
    pixels: context.getImageData(0, 0, width, height).data,
  };
};

/**
 * Extracts a card's gradient colors.
 *
 * @param {string} imageUrl - Source to sample, which may be a derived blob or data URI.
 * @param {{ cacheKey?: string, persist?: boolean }} [options] - `cacheKey` is the
 *   stable identity of the underlying asset; only an http(s) key is persisted,
 *   since a blob or data URI is either dead or ruinously large as a storage key.
 *   `persist: false` still reads that key but never writes it, for bytes that
 *   are not the canonical derivative.
 * @returns {Promise<{dark: string, vibrant: string, lightVibrant: string, lightMuted: string, darkMuted: string, ink: import("@stanza/shared/color-contrast").InkProfile | null}>}
 */
export const extractImageColors = async (imageUrl, { cacheKey, persist = true } = {}) => {
  const key = canonicalImageKey(cacheKey ?? imageUrl);
  const paletteKey = key?.startsWith("http") ? key : null;
  const persisted = paletteKey ? imagePalettes.get(paletteKey) : undefined;
  if (persisted) return persisted;

  const memoKey =
    key && key.length <= MAX_COLOR_CACHE_KEY_LENGTH ? key : null;
  if (memoKey) {
    const memoized = colorExtractionCache.get(memoKey);
    if (memoized) {
      colorExtractionCache.delete(memoKey);
      colorExtractionCache.set(memoKey, memoized);
      return memoized;
    }
    const inFlight = colorExtractionRequests.get(memoKey);
    if (inFlight) return inFlight;
  }

  const swatchHex = (swatch) => swatch?.getHex() ?? FALLBACK_SWATCH_HEX;
  const request = (async () => {
    const { dataUrl, pixels } = await resizeImageForColorExtraction(imageUrl);
    const pallete = await Vibrant.from(dataUrl)
      .maxDimension(COLOR_EXTRACTION_MAX_DIMENSION)
      .getPalette();
    const colors = {
      dark: swatchHex(pallete.DarkVibrant),
      vibrant: swatchHex(pallete.Vibrant),
      lightVibrant: swatchHex(pallete.LightVibrant),
      lightMuted: swatchHex(pallete.LightMuted),
      darkMuted: swatchHex(pallete.DarkMuted),
      ink: inkProfileFromPixels(pixels),
    };

    // Colors sampled from a copy of a since-replaced image must not outlive this load.
    if (persist && colors.ink && paletteKey && !imageCacheStorage.sourceChangedThisLoad(paletteKey)) {
      imagePalettes.set(paletteKey, colors);
    }
    if (memoKey) {
      colorExtractionCache.set(memoKey, colors);
      while (colorExtractionCache.size > MAX_COLOR_EXTRACTIONS) {
        colorExtractionCache.delete(colorExtractionCache.keys().next().value);
      }
    }
    return colors;
  })();

  if (memoKey) {
    colorExtractionRequests.set(memoKey, request);
    request.finally(() => colorExtractionRequests.delete(memoKey)).catch(() => {});
  }
  return request;
};

/**
 * Why an email address can't be used, or null when it can.
 *
 * @param {string} string
 * @returns {"invalid" | "blocked" | null}
 */
export const getEmailValidationError = (string) => {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(string ?? "")) return "invalid";
  if (temporaryEmailsSet.has(string.split("@")[1].toLowerCase())) return "blocked";
  return null;
};

export const isInvalidEmail = (string) => getEmailValidationError(string) !== null;

/**
 * Parses a date value for display.
 *
 * @param {string} dateStr - A date, UTC or local date-time value.
 * @param {string} [tzid] - IANA zone local date-times are read in; the viewer's zone when omitted.
 * @returns {{ eventParsed: Date, isAllDayEvent: boolean }}
 */
export const parseDateEvent = (dateStr, tzid) => {
  // A date-only value names a civil day, so it starts at the viewer's local midnight.
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr ?? "");
  if (day) {
    const [, year, month, date] = day.map(Number);
    const local = new Date(year, month - 1, date);
    const isCalendarDay =
      local.getFullYear() === year && local.getMonth() === month - 1 && local.getDate() === date;
    return { eventParsed: isCalendarDay ? local : new Date(NaN), isAllDayEvent: true };
  }
  return {
    eventParsed: zonedWallClock(dateStr, tzid) ?? new Date(dateStr),
    isAllDayEvent: false,
  };
};

/**
 * Parses a jCal DTSTART/DTEND property, reading a local date-time in the property's TZID.
 *
 * @param {Array} prop - jCal property: [name, params, type, value].
 * @returns {{ eventParsed: Date, isAllDayEvent: boolean }}
 */
export const parseJcalDate = (prop) => parseDateEvent(prop?.[3] ?? "", prop?.[1]?.tzid);

/**
 * Long start date ("October 17, 2026") of the unfinished event in an ICS body that ends first.
 *
 * @param {string} ics - ICS calendar body.
 * @returns {string|null} The date, or null when no event is upcoming.
 * @throws {Error} When the body is not parseable ICS.
 */
export const nextEventDateLabel = (ics) => {
  const vevents = new ical.Component(ical.parse(ics)).getAllSubcomponents("vevent");
  const upcoming = selectUpcomingEvents(vevents.map((event) => ({ jCal: event.toJSON() })));
  // Earliest end, so a long event already under way doesn't hide the next one.
  const { event: next } = upcoming.reduce(
    (soonest, event) => {
      const { dtend } = getEventDates(event);
      return dtend < soonest.dtend ? { event, dtend } : soonest;
    },
    { event: null, dtend: Infinity }
  );
  const dtstart = next?.jCal[1].find((prop) => prop[0] === "dtstart");
  return dtstart
    ? parseJcalDate(dtstart).eventParsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
};

/**
 * Formats a start as "Today at …", "Tomorrow at …", "<date> | <time>" or "All Day".
 *
 * @param {string|Array} event - A date value, or a jCal DTSTART property.
 * @param {object} snackbarContext - Snackbar setters used to report an unparseable date.
 * @returns {string}
 */
export const formatDate = (event, snackbarContext) => {
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = snackbarContext;
  try {
    if (!(Array.isArray(event) ? event[3] : event)) {
      return "";
    }
    let { eventParsed, isAllDayEvent } = Array.isArray(event)
      ? parseJcalDate(event)
      : parseDateEvent(event);
    const currentDate = new Date();
    const tomorrowDate = new Date(currentDate);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);

    const eventDate = eventParsed.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    const eventTime = eventParsed.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    if (isAllDayEvent) {
      return "All Day";
    } else if (eventParsed.toDateString() === currentDate.toDateString()) {
      return `Today at ${eventTime}`;
    } else if (eventParsed.toDateString() === tomorrowDate.toDateString()) {
      return `Tomorrow at ${eventTime}`;
    } else {
      return `${eventDate} | ${eventTime}`;
    }
  } catch (error) {
    console.error(error);
    showSnackBar(
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
      "This browser isn't displaying dates correctly. Please use another browser.",
      "error"
    );
    return "Unknown date/time";
  }
};

// Create the .ics for the user to download an individual event
export const convertToIcs = (jCal) => {
  if (!jCal || !Array.isArray(jCal)) {
    console.error("Invalid jCal data");
    return "";
  }
  // Ensure the root component is VCALENDAR
  let comp;
  if (jCal[0] === "vcalendar") {
    // jCal represents a complete calendar object
    comp = new ICAL.Component(jCal);
  } else {
    // jCal represents an individual event (VEVENT), wrap it in a VCALENDAR component
    comp = new ICAL.Component(["vcalendar", [], []]);
    comp.addSubcomponent(new ICAL.Component(jCal));
  }
  // Convert the component to iCalendar format string
  const icsContent = comp.toString();
  return icsContent;
};

export const downloadIcsFileFromjCal = (jCal, eventSummary) => {
  // Logic for downloading the .ics file
  const icsContent = convertToIcs(jCal);
  const blob = new Blob([icsContent], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);

  // Create a link element
  const link = document.createElement("a");
  link.href = url;

  // Set the download attribute with a filename
  const eventName = eventSummary ? eventSummary[3] : "event";
  link.download = `${eventName}.ics`;
  const keydownEvent = (e) => {
    if (e.key === "d") {
      const event = new MouseEvent("click", {});
      link.dispatchEvent(event);
    }
  };
  document.addEventListener("keydown", keydownEvent, { once: true });

  // Programmatically click the link to trigger the download by an event
  document.body.appendChild(link);
  const event = new KeyboardEvent("keydown", { key: "d" });
  document.dispatchEvent(event);

  // Remove the link from the document
  document.body.removeChild(link);

  // Release the object URL
  URL.revokeObjectURL(url);
};

const MAX_CACHED_IMAGES = 50;
const imageCache = new Map();
const imageRequests = new Map();
/** Bumped per image when it is forgotten, so a read in flight cannot re-cache it. */
const imageGenerations = new Map();
const generationOf = (url) => imageGenerations.get(canonicalImageKey(url)) ?? 0;

/**
 * Convert the fetched blob to a data URL instead of an object URL. A data URL
 * remains valid for an image that is already mounted even after it is evicted
 * from this cache, so the LRU can have a hard bound without revoking live URLs.
 */
const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Failed to read image."));
    reader.onabort = () => reject(new Error("Image read was aborted."));
    reader.readAsDataURL(blob);
  });

const cacheImage = (imageUrl, uri) => {
  imageCache.delete(imageUrl);
  imageCache.set(imageUrl, uri);

  while (imageCache.size > MAX_CACHED_IMAGES) {
    const oldestImageUrl = imageCache.keys().next().value;
    imageCache.delete(oldestImageUrl);
  }
};

export const fetchAndCacheImage = async (imageUrl, setImageUri) => {
  const cachedUri = imageCache.get(imageUrl);
  if (cachedUri) {
    cacheImage(imageUrl, cachedUri);
    setImageUri?.(cachedUri);
    return cachedUri;
  }

  let request = imageRequests.get(imageUrl);
  if (!request) {
    const generation = generationOf(imageUrl);
    // Bytes that arrive after the image was forgotten are the outgoing ones.
    const stillCurrent = () => generationOf(imageUrl) === generation;
    request = (async () => {
      // Cache Storage sits between the in-memory map and the network, so a cold
      // start reuses bytes from a previous visit instead of downloading them.
      const persisted = await imageCacheStorage.getOriginal(imageUrl);
      if (persisted) {
        if (persisted.stale) imageCacheStorage.revalidate(imageUrl);
        const uri = await blobToDataUrl(persisted.blob);
        if (stillCurrent()) cacheImage(imageUrl, uri);
        return uri;
      }

      // Not force-cache: that serves a replaced image's old bytes forever.
      const response = await fetch(imageUrl, {
        cache: "default",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch image.");
      }

      const blob = await response.blob();
      const uri = await blobToDataUrl(blob);
      if (stillCurrent()) {
        cacheImage(imageUrl, uri);
        imageCacheStorage.putOriginal(imageUrl, blob);
      }
      return uri;
    })();
    imageRequests.set(imageUrl, request);
  }

  try {
    const uri = await request;
    setImageUri?.(uri);
    return uri;
  } catch (error) {
    console.error(error);
    throw new Error("Failed to fetch image.");
  } finally {
    if (imageRequests.get(imageUrl) === request) {
      imageRequests.delete(imageUrl);
    }
  }
};

/**
 * Drops an image's in-memory bytes and palette so the next read refetches them.
 * @param {string|null|undefined} url
 */
export const forgetCachedImage = (url) => {
  const key = canonicalImageKey(url);
  if (!key) return;
  for (const cachedUrl of imageCache.keys()) {
    if (canonicalImageKey(cachedUrl) === key) imageCache.delete(cachedUrl);
  }
  // An in-flight read would otherwise put the outgoing bytes straight back.
  imageGenerations.set(key, (imageGenerations.get(key) ?? 0) + 1);
  for (const requestedUrl of imageRequests.keys()) {
    if (canonicalImageKey(requestedUrl) === key) imageRequests.delete(requestedUrl);
  }
  colorExtractionCache.delete(key);
};

export const copyCalendarLinkToClipboard = ({
  inGroup,
  user,
  calendar,
  showSnackBar,
  setSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
}) => {
  let calendarLink;
  if (inGroup) {
    calendarLink = getCalendarApiUrl({
      calendar: user.createdGroups.find((group) =>
        group.calendars.includes(calendar._id)
      ),
      user,
    });
  } else {
    calendarLink = getCalendarApiUrl({ calendar, user });
  }
  if (!calendarLink) {
    showSnackBar(
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
      "There was an issue copying the link. Please try again.",
      "error"
    );
    console.error("Missing webcal URL for calendar", calendar?.handle, calendar?._id);
    return;
  }
  if (navigator.clipboard && "writeText" in navigator.clipboard) {
    navigator.clipboard
      .writeText(getWebcalFormat(calendarLink))
      .then(() => {
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          "Calendar link copied successfully.",
          "success"
        );
        console.log("Link copied to clipboard!");
      })
      .catch((err) => {
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          "There was an issue copying the link. Please try again.",
          "error"
        );
        console.error("Failed to copy: ", err);
      });
  } else {
    // Android browsers don't support clipboard API
    const input = document.createElement("input");
    input.value = getWebcalFormat(calendarLink);
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
    showSnackBar(
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
      "Calendar link copied successfully.",
      "success"
    );
  }
};

const handleSimulateClickEvent = (link) => {
  const keydownEvent = (e) => {
    if (e.key === "d") {
      const event = new MouseEvent("click", {});
      link.dispatchEvent(event);
    }
  };
  document.addEventListener("keydown", keydownEvent, { once: true });
  const event = new KeyboardEvent("keydown", { key: "d" });
  document.dispatchEvent(event);
};

/**
 * Opens a webcal URL in the user's calendar app (or Google's add page).
 *
 * @throws {Error} When `url` is missing, so callers never report a subscribe that didn't open.
 */
export const handleOpenWebcal = (
  user,
  url,
  calendar,
  skipRestrictions = false,
  downloadSample = false
) => {
  if (hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key) && !skipRestrictions)
    return;
  assertWebcalUrl(url, calendar);
  // Route Google-provider users through calendar/render?cid (incl. Android,
  // where webcal:// has no registered handler and window.open silently no-ops).
  if (user.provider === "google" && !downloadSample) {
    const link = document.createElement("a");
    const googleUrl = `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(
      url
    )}`;
    link.href = googleUrl;
    link.target = "_blank";
    link.click();
  } else if (navigator.userAgent.match(/(iPod|iPhone|iPad|Safari|Mac)/)) {
    const link = document.createElement("a");
    link.href = url;
    link.download = `${calendar?.name}.ics`;
    if (
      navigator.userAgent.match(/Mac/) &&
      !navigator.userAgent.match(/(iPod|iPhone|iPad)/)
    ) {
      link.target = "_blank";
    }

    // Programmatically click the link to trigger the download by an event
    document.body.appendChild(link);
    handleSimulateClickEvent(link);

    // Remove the link from the document
    document.body.removeChild(link);
  } else {
    window.open(url, "_blank");
  }
};

export const handleUnsubscribe = async ({
  user,
  calendar,
  unsubscribeGroup,
  createdGroups,
  removeFromGroup,
  unsubscribeCalendar,
  onUnsubscribed = () => { },
  showSnackBar,
  setSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
  setSubscribed = () => { },
}) => {
  try {
    setSubscribed(false);
    const createdGroup = createdGroups?.find((group) =>
      group.calendars.some(
        (groupCalendar) => groupCalendar._id === calendar._id
      )
    );
    // Determine the type of unsubscription and perform the appropriate action
    switch (true) {
      case user?.addedCalendars?.includes(calendar._id):
        await unsubscribeCalendar({ calendarId: calendar._id });
        await onUnsubscribed("unsubscribeCalendar");
        break;
      case user?.addedGroups?.includes(calendar._id):
        await unsubscribeGroup({ groupId: calendar._id });
        await onUnsubscribed("unsubscribeGroup");
        break;
      case Boolean(createdGroup):
        await removeFromGroup({
          calendarId: calendar._id,
          groupId: createdGroup._id,
        });
        await onUnsubscribed("removeFromGroup");
        break;
      default:
        throw new Error("Calendar or group not found in user's subscriptions.");
    }
  } catch (error) {
    setSubscribed(true);
    console.error(error);
    showSnackBar(
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
      error.message || "An error occurred. Please try again.",
      "error"
    );
  }
};

export function getMinuteTimestamp() {
  const now = new Date();
  return Math.floor(now.getTime() / (1000 * 60));
}

export function timeAgo(date) {
  const now = dayjs();
  const diffMinutes = now.diff(date, "minute");
  const diffHours = now.diff(date, "hour");
  const diffDays = now.diff(date, "day");

  if (diffMinutes < 1) {
    return `Just now`;
  } else if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
  } else {
    return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
  }
}

// Utility function to format dates for ICS files
function formatDateForIcs(date) {
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

// Function to generate the ICS file content
export function generateGCalReminder(calendarName, calendarLink) {
  const now = new Date();

  // Create a Date object for 1 hour from now
  const startDate = new Date(now.getTime() + 60 * 60 * 1000);
  const endDate = new Date(startDate.getTime() + 15 * 60 * 1000); // 15 minutes later

  const dtstart = formatDateForIcs(startDate);
  const dtend = formatDateForIcs(endDate);
  const dtstamp = formatDateForIcs(now);
  const uid = `reminder-${now.getTime()}@stanzacal.com`;

  // Instructions to include in the event description
  const description = `
Instructions for syncing with Google Calendar:

🖥️
1. Open Google Calendar on your desktop (https://calendar.google.com).
2. Click the "+" button next to "Other calendars" in the left panel.
3. Select "From URL" and paste this calendar URL: ${calendarLink}
4. Click "Add calendar".

📱
Now go to your Google Calendar app on your phone and find the new calendar.

1. Tap on ≡ on the top left. Scroll down and tap on the Settings menu item. This entry is almost at the end of the list.
2. Find ${calendarName} in the list. If you don't see the calendar in the list, scroll down to the bottom and tap on "Show more". 
3. Once you find the calendar, tap on it and toggle the "Sync"​​ button (first setting on top of the screen) to turn sync on.

✔ The calendar is now synced with your Google Calendar app. You should see the events in your calendar shortly.

You can delete this event once you're done syncing.
`.trim();

  // Generate the ICS file content
  const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Stanza//Calendar Reminder//EN
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${dtstamp}
DTSTART:${dtstart}
DTEND:${dtend}
SUMMARY:🔔 Sync ${calendarName} with Google Calendar
DESCRIPTION:${description.replace(/\n/g, "\\n")}
END:VEVENT
END:VCALENDAR
`.trim();

  return icsContent;
}

/**
 * Routes an add or re-add to the right channel for this user's Google Connect state.
 *
 * @param {Object} params
 * @param {Object} params.user
 * @param {Function} params.navigate - react-router navigate
 * @param {Object} params.calendar
 * @param {Function} params.defaultFn - opens the calendar by link
 * @param {Function} [params.onHasValidGoogleConnection] - called when the connection is healthy
 * @param {Function} [params.onChannelIntercept] - called with the decision for a broken or lapsed connection
 * @returns {boolean} true when the add went to the channel intercept instead of a channel.
 */
export const handleSubscribeExperience = ({
  user,
  navigate,
  calendar,
  defaultFn,
  onHasValidGoogleConnection,
  onChannelIntercept,
}) => {
  const channelState = googleConnectState(user);
  if (
    onChannelIntercept &&
    (channelState === GOOGLE_CONNECT_STATES.BROKEN ||
      channelState === GOOGLE_CONNECT_STATES.INACTIVE)
  ) {
    onChannelIntercept({
      state: channelState,
      calendar,
      // The link path re-runs the routing the intercept pre-empted; the
      // destination flow performs the subscribe.
      onAddByLink: () =>
        handleSubscribeExperience({
          user,
          navigate,
          calendar,
          defaultFn,
          onHasValidGoogleConnection,
        }),
    });
    return true;
  }

  if (shouldUseSubscriptionExperience(user)) {
    // Concierge kill-switch: the chat flow only takes the slot when the flag
    // is on; default stays the steps wizard. Entities missing a handle or a
    // signed-in user id also stay on the wizard — the concierge route's path
    // params need both.
    if (
      process.env.REACT_APP_CONCIERGE_SYNC === "true" &&
      calendar?.handle &&
      user?._id
    ) {
      const { variant } = abTestingService.getAssignment(
        "CONCIERGE_SYNC_FLOW",
        user,
        { calendar }
      );
      if (variant?.name === "concierge") {
        navigate(
          getPageRoute("help", "CONCIERGE", {
            handle: calendar.handle,
            calendarId: calendar?._id,
          }),
          { state: { calendar } }
        );
        return false;
      }
    }
    const isAndroid = new UAParser().getResult().os.name === "Android";
    navigate(
      getPageRoute(
        "help",
        isAndroid ? "SUBSCRIBING_GOOGLE_CALENDAR" : "SUBSCRIBING_CALENDAR",
        { handle: calendar?.handle, calendarId: calendar?._id }
      ),
      { state: { calendar } }
    );
  } else if (hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key)) {
    onHasValidGoogleConnection?.();
  } else {
    // Stanza+ users on a non-Google provider open the webcal directly.
    defaultFn();
  }
  return false;
};

/**
 * Whether an add can be shown as done: it subscribed here, or it went on to a
 * channel rather than to the intercept, whose choice still has to subscribe.
 *
 * @param {{ subscribedHere: boolean, intercepted: boolean }} params
 * @returns {boolean}
 */
export const isAddComplete = ({ subscribedHere, intercepted }) =>
  subscribedHere || !intercepted;

/**
 * Builds the per-user webcal feed URL for a calendar or group.
 *
 * @param {{ calendar: { stanzaLink?: string }, user: { id?: string } }} params
 * @returns {string|null} Null when the calendar carries no `stanzaLink`.
 */
export function getCalendarApiUrl({ calendar, user }) {
  if (!calendar?.stanzaLink) return null;
  const updated = encodeURIComponent(new Date().toISOString());
  // Auth users carry `_id`; some callers pass `id`. Accept both so no caller
  // can build a `.../undefined.ics` link.
  return `${calendar?.stanzaLink}/${user?.id ?? user?._id}.ics?updated=${updated}`;
}

/**
 * Opens the calendar's per-user webcal feed when its URL can be built.
 *
 * @param {{ user: object, calendar: { stanzaLink?: string } }} params
 * @returns {boolean} False when the calendar has no `stanzaLink`, so nothing was opened.
 */
export function openCalendarWebcal({ user, calendar }) {
  const url = getCalendarApiUrl({ calendar, user });
  if (!url) return false;
  handleOpenWebcal(user, getWebcalFormat(url), calendar);
  return true;
}

/**
 * Builds the per-user feed URL embedded in the Google Calendar reminder `.ics`.
 *
 * @param {{ calendar: { stanzaLink?: string }, user: { _id?: string } }} params
 * @returns {string|null} Null when the calendar carries no `stanzaLink`.
 */
export function getCalendarReminderUrl({ calendar, user }) {
  if (!calendar?.stanzaLink) return null;
  return `${calendar.stanzaLink}/${user?._id}.ics`;
}

/**
 * Throws when a webcal URL could not be built, so no flow opens or saves a broken link.
 *
 * @param {string|null} url
 * @param {{ handle?: string, _id?: string }} calendar
 * @throws {Error} When `url` is missing.
 */
export function assertWebcalUrl(url, calendar) {
  if (!url) {
    throw new Error(
      `Missing webcal URL for calendar ${calendar?.handle}/${calendar?._id}`
    );
  }
}

export const handleUpgrade = async ({
  user,
  searchParams,
  navigate,
  successUrl = null,
  state = {},
}) => {
  // Add success param to the URL
  if (successUrl) {
    const successUrlSearchParams = new URLSearchParams(
      successUrl.split("?")?.[1] ?? ""
    );
    successUrlSearchParams.set("success", "true");
    successUrl = `${successUrl.split("?")[0]
      }?${successUrlSearchParams.toString()}`;
  }

  // If no source is provided, detect the current page
  let source = state?.source;
  if (!source) {
    const currentPath = window.location.pathname;
    // Convert pathname to a readable source identifier
    if (currentPath === "/") {
      source = "home_page";
    } else {
      // Convert path like "/library" or "/upgrade/plus" to "library_page" or "upgrade_page"
      const pathSegments = currentPath.split("/").filter(Boolean);
      source =
        pathSegments.length > 0 ? `${pathSegments[0]}_page` : "unknown_page";
    }
  }

  navigate(
    getPageRoute(
      "upgrade",
      "UPGRADE_PLUS",
      {},
      successUrl ? { successUrl: encodeURIComponent(successUrl) } : {}
    ),
    { state: { ...state, source } }
  );
};

export function safeParse(jsonString) {
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
}

export function getObjectDepth(obj) {
  if (typeof obj !== "object" || obj === null) {
    return 0;
  }
  if (obj.$$typeof && typeof obj.$$typeof === "symbol") {
    return 0;
  }

  let maxDepth = 0;

  for (const key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      maxDepth = Math.max(maxDepth, getObjectDepth(obj[key]));
    }
  }

  return maxDepth + 1;
}

/**
 * Handle resubscribe flow (shared by Library and SubscriptionVerificationBanner)
 *
 * - Centralizes feature access check + default subscription experience
 * - Allows callers to customize what happens for Google provider via onGoogleProvider
 * - Optionally sets selected calendar in caller via setSelectedCalendar
 *
 * @param {Object} params
 * @param {Object} params.user
 * @param {Object} params.calendar
 * @param {Function} params.navigate - react-router navigate
 * @param {Function} [params.requestFeatureAccess] - from useFeatureAccess()
 * @param {Function} [params.onGoogleProvider] - custom behavior when user.provider === 'google'
 * @param {Function} [params.onHasValidGoogleConnection] - custom behavior for a healthy Google Connect user
 * @param {Function} [params.onChannelIntercept] - opens the channel decision for a broken or lapsed connection
 * @param {Function} [params.onMissingWebcalUrl] - called when no webcal URL can be built for the calendar
 * @param {Function} [params.setSelectedCalendar] - optional setter to store selected calendar
 */
export const handleResubscribe = async ({
  user,
  calendar,
  navigate,
  requestFeatureAccess,
  onGoogleProvider,
  onHasValidGoogleConnection,
  onChannelIntercept,
  onMissingWebcalUrl,
  setSelectedCalendar,
  inGroup,
  calendarGroup,
}) => {
  // Resolve correct calendar context (group vs standalone)
  const targetCalendar = inGroup && calendarGroup ? calendarGroup : calendar;

  // Optional: store calendar context for dialogs in the caller
  if (typeof setSelectedCalendar === "function") {
    setSelectedCalendar({ action: "resubscribe", calendar: targetCalendar });
  }

  const performResubscribe = async ({ user }) => {
    const openWebcal = () => {
      if (openCalendarWebcal({ user, calendar: targetCalendar })) return;
      if (onMissingWebcalUrl) onMissingWebcalUrl();
      else assertWebcalUrl(null, targetCalendar);
    };

    const defaultFn = () => {
      if (user?.provider === "google" && typeof onGoogleProvider === "function") {
        onGoogleProvider();
      } else {
        // Non-Google users, and Google users with no custom flow, open the webcal.
        openWebcal();
      }
    };

    handleSubscribeExperience({
      user,
      navigate,
      calendar: targetCalendar,
      defaultFn,
      onHasValidGoogleConnection,
      onChannelIntercept,
    });
  };

  // If FeatureAccess is provided, respect gating + simple payment flow
  if (typeof requestFeatureAccess === "function") {
    const result = await requestFeatureAccess(
      Features.RESUBSCRIBE_CALENDAR.key,
      {
        calendar: targetCalendar,
        currentPath: window.location.pathname,
        action: performResubscribe,
        continueText: "Continue Resubscribing",
      }
    );

    if (result.granted && result.reason === "has_access") {
      await performResubscribe({ user });
    }
  } else {
    // If no gating provided, proceed with default behavior
    await performResubscribe({ user });
  }
};

export const isEmbeddedPath = (location) => {
  const { href } = location;
  return href.includes("/timeline_embedded") || href.includes("/embedded");
};

const WHITE_MIN = 240;
const BLACK_MAX = 15;
// Processed images never render above ~166px CSS, so this clears 3x DPR.
const MAX_PROCESSED_EDGE = 512;

function floodFillBackground(data, width, height, isWhite) {
  const pixelCount = width * height;
  const visited = new Uint8Array(pixelCount);
  const queue = [];

  const matches = isWhite
    ? (i) => data[i] >= WHITE_MIN && data[i + 1] >= WHITE_MIN && data[i + 2] >= WHITE_MIN && data[i + 3] === 255
    : (i) => data[i] <= BLACK_MAX && data[i + 1] <= BLACK_MAX && data[i + 2] <= BLACK_MAX && data[i + 3] === 255;

  // Seed from all 4 edges
  for (let x = 0; x < width; x++) {
    for (const pixelIdx of [x, (height - 1) * width + x]) {
      if (!visited[pixelIdx] && matches(pixelIdx * 4)) {
        visited[pixelIdx] = 1;
        queue.push(pixelIdx);
      }
    }
  }
  for (let y = 1; y < height - 1; y++) {
    for (const pixelIdx of [y * width, y * width + (width - 1)]) {
      if (!visited[pixelIdx] && matches(pixelIdx * 4)) {
        visited[pixelIdx] = 1;
        queue.push(pixelIdx);
      }
    }
  }

  // BFS
  let head = 0;
  while (head < queue.length) {
    const pixelIdx = queue[head++];
    data[pixelIdx * 4 + 3] = 0;

    const x = pixelIdx % width;
    const y = Math.floor(pixelIdx / width);
    const neighbors = [
      y > 0 ? pixelIdx - width : -1,
      y < height - 1 ? pixelIdx + width : -1,
      x > 0 ? pixelIdx - 1 : -1,
      x < width - 1 ? pixelIdx + 1 : -1,
    ];
    for (const n of neighbors) {
      if (n >= 0 && !visited[n] && matches(n * 4)) {
        visited[n] = 1;
        queue.push(n);
      }
    }
  }
}

/**
 * Makes the flat white border of a logo transparent, downscaling past
 * `MAX_PROCESSED_EDGE` first so the per-pixel flood fill stays bounded.
 *
 * @param {string} imageUri
 * @returns {Promise<Blob>} The processed PNG.
 */
export const removeImageBackgroundBlob = (imageUri) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = imageUri;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const scale = Math.min(1, MAX_PROCESSED_EDGE / Math.max(img.width, img.height));
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Only remove background if the top-left pixel is white/near-white or black/near-black
      const r = data[0];
      const g = data[1];
      const b = data[2];
      const a = data[3];
      const isWhiteBg = r >= WHITE_MIN && g >= WHITE_MIN && b >= WHITE_MIN && a === 255;
      const isBlackBg = r <= BLACK_MAX && g <= BLACK_MAX && b <= BLACK_MAX && a === 255;

      if (isWhiteBg) {
        floodFillBackground(data, canvas.width, canvas.height, true);
      } else if (isBlackBg) {
        // disable black removal as some images have black on the corners
        // floodFillBackground(data, canvas.width, canvas.height, false);
      }

      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to encode processed image."));
      }, "image/png");
    };
    img.onerror = (error) => {
      reject(error);
    };
  });
};

/**
 * @param {string} imageUri
 * @returns {Promise<string>} Object URL of the processed PNG.
 */
export const removeImageBackground = async (imageUri) => {
  // An object URL over a base64 data URL: ~4x less retained memory, and it
  // stays a short string so consumers can use it as a cache/query key.
  return URL.createObjectURL(await removeImageBackgroundBlob(imageUri));
};
